import { ProtectedRoute } from '@/auth/ProtectedRoute';
import { StaffGuard } from '@/auth/guard';
import { RouteErrorPage } from '@/components/RouteErrorPage';
import { FinanceLayout } from '@/layouts/FinanceLayout';
import { PermissionsProvider } from '@/layouts/PermissionsContext';
import { lazyWithRecovery } from '@/lib/lazyWithRecovery';
import LoginPage from '@/pages/login';
import SsoPage from '@/pages/sso';
import { Spin } from 'antd';
import { type ReactNode, Suspense } from 'react';
import { Navigate, createBrowserRouter } from 'react-router-dom';
import FinanceHome from './home';

const MyClaimsPage = lazyWithRecovery(() => import('@/pages/my-claims'));
const MyClaimFormPage = lazyWithRecovery(() => import('@/pages/my-claims/form'));
const MyClaimDetailPage = lazyWithRecovery(() => import('@/pages/my-claims/detail'));
const MyProfilePage = lazyWithRecovery(() => import('@/pages/my-profile'));
const ReviewPage = lazyWithRecovery(() => import('@/pages/review'));
const CashierPage = lazyWithRecovery(() => import('@/pages/cashier'));
const EmployeesPage = lazyWithRecovery(() => import('@/pages/employees'));
const DepartmentsPage = lazyWithRecovery(() => import('@/pages/departments'));
const AuditPage = lazyWithRecovery(() => import('@/pages/audit'));
const OverviewPage = lazyWithRecovery(() => import('@/pages/overview'));
const ClaimsPage = lazyWithRecovery(() => import('@/pages/claims'));
const ClaimDetailPage = lazyWithRecovery(() => import('@/pages/claims/detail'));
const TransactionsPage = lazyWithRecovery(() => import('@/pages/transactions'));
const ObligationsPage = lazyWithRecovery(() => import('@/pages/obligations'));
const AccountsPage = lazyWithRecovery(() => import('@/pages/accounts'));
const CategoriesPage = lazyWithRecovery(() => import('@/pages/categories'));
const SupplierStatementsPage = lazyWithRecovery(() => import('@/pages/supplier-statements'));
const ReportsPage = lazyWithRecovery(() => import('@/pages/reports'));

function deferred(page: ReactNode) {
  return (
    <Suspense
      fallback={
        <div style={{ display: 'grid', minHeight: 240, placeItems: 'center' }}>
          <Spin tip="正在加载功能" />
        </div>
      }
    >
      {page}
    </Suspense>
  );
}

/**
 * 财务系统路由表（部署在 finance.<host>，base '/'）：
 *   /login  统一身份登录（OIDC PKCE）或后台专用账号应急登录
 *   /sso    带 token 免二次登录的落地页
 *   /*      受保护 + 员工身份校验 + 权限位过滤后的财务页
 *
 * 鉴权分三层：ProtectedRoute（有无 token）→ StaffGuard（后端 /auth/me 是否是有效
 * 员工）→ FinanceLayout（按 /ext/finance/me 的岗位权限位放行具体页面，直接敲 URL
 * 也拦；未建档的账号整页阻断）。
 */
const basename = (import.meta.env.BASE_URL || '/').replace(/\/$/, '') || undefined;

export const router = createBrowserRouter(
  [
    { path: '/login', element: <LoginPage /> },
    { path: '/sso', element: <SsoPage /> },
    {
      path: '/',
      errorElement: <RouteErrorPage />,
      element: (
        <ProtectedRoute>
          <StaffGuard>
            <PermissionsProvider>
              <FinanceLayout />
            </PermissionsProvider>
          </StaffGuard>
        </ProtectedRoute>
      ),
      children: [
        { index: true, element: <FinanceHome /> },
        { path: 'my/claims', element: deferred(<MyClaimsPage />) },
        { path: 'my/claims/new', element: deferred(<MyClaimFormPage />) },
        { path: 'my/claims/:id', element: deferred(<MyClaimDetailPage />) },
        { path: 'my/claims/:id/edit', element: deferred(<MyClaimFormPage />) },
        { path: 'my/profile', element: deferred(<MyProfilePage />) },
        { path: 'review', element: deferred(<ReviewPage />) },
        { path: 'cashier', element: deferred(<CashierPage />) },
        { path: 'overview', element: deferred(<OverviewPage />) },
        { path: 'claims', element: deferred(<ClaimsPage />) },
        { path: 'claims/:id', element: deferred(<ClaimDetailPage />) },
        { path: 'transactions', element: deferred(<TransactionsPage />) },
        { path: 'obligations', element: deferred(<ObligationsPage />) },
        { path: 'accounts', element: deferred(<AccountsPage />) },
        { path: 'categories', element: deferred(<CategoriesPage />) },
        { path: 'supplier-statements', element: deferred(<SupplierStatementsPage />) },
        { path: 'reports', element: deferred(<ReportsPage />) },
        { path: 'employees', element: deferred(<EmployeesPage />) },
        { path: 'departments', element: deferred(<DepartmentsPage />) },
        { path: 'audit', element: deferred(<AuditPage />) },
      ],
    },
    { path: '*', element: <Navigate to="/" replace /> },
  ],
  { basename },
);
